import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import GenericFormDrawer from '../../../../components/GenericFormDrawer';
import Button from '../../../../components/ui/Button';
import TextInput from '../../../../components/ui/TextInput';
import CheckboxInput from '../../../../components/ui/CheckboxInput';
import { saveReportSettings } from '../../../../services/surveyReportService';
import {
  MAX_BREAKDOWNS, settingsToForm, formToSettings, breakdownChoices, isBreakdownChecked, toggleBreakdown,
} from './structureModel';

// Spec 2026-10-16 S5 — sade Rapor Ayarları: üstte "Raporda Hangi Kırılımlar Olsun" kutucukları, altta kapalı "Gelişmiş"
// eşikler (varsayılanlarla çalışır). Birim grupları, ısı haritası satırları ve uygunluk ekranda YOK; kayıtlı değerleri
// formToSettings korur (netleşen 14). Kırılım soruları anketin sorularından (GetById → isBreakdown) gelir. Public ankette
// kayıtlı İK kırılımları ve birim grupları forma alınmaz ve kayıtta düşer (sunucu doğrulayıcısı reddeder).

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Row2 = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Hint = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Boxes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Divider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const Error = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

export default function ReportSettingsDrawer({ open, onClose, surveyId, settings, isPublic, questions, onSaved }) {
  const { t } = useTranslation();
  const publicSurvey = !!isPublic;
  const [form, setForm] = useState(() => settingsToForm(settings, publicSurvey));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [error, setError] = useState(null);
  const choices = useMemo(() => breakdownChoices(t, publicSurvey, questions), [t, publicSurvey, questions]);

  // M1 — form yalnız AÇILIŞTA sıfırlanır: çekmece açıkken rapor yeniden yüklenirse (AI işi bitti, yoklama) yeni `settings`
  // nesnesi gelir ama kaydedilmemiş seçimler korunur.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setForm(settingsToForm(settings, publicSurvey));
      setAdvancedOpen(false);
      setError(null);
    }
    wasOpen.current = open;
  }, [open, settings, publicSurvey]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const full = form.breakdowns.length >= MAX_BREAKDOWNS;
  // M5 — eski çekmeceyle kaydedilmiş "birim grubu" kırılımı bir kontenjan kullanır; görünür (işaretli) satır olarak gösterilir,
  // işaret kaldırılınca düşer.
  const hasDepartmentGroup = !publicSurvey && form.breakdowns.some((b) => b.kind === 'departmentGroup');

  const handleSave = async () => {
    const parsed = formToSettings(form, settings, choices, publicSurvey);
    if (parsed.error) {
      setError(parsed.error);
      if (parsed.error !== 'survey.report.invalidSettings.departmentAndGroup') setAdvancedOpen(true);
      return false;
    }
    setError(null);
    await saveReportSettings(surveyId, parsed.value);
    onSaved?.();
    return true;
  };

  return (
    <GenericFormDrawer open={open} onClose={onClose} title={t('survey.report.settings.title')} schema={[]} onSave={handleSave}>
      <Stack>
        {error && <Error>{t(`responseErrors.${error}`)}</Error>}
        <div>
          <Title>{t('survey.report.settings.breakdownsTitle')}</Title>
          <Hint>{t('survey.report.settings.breakdownsHint', { max: MAX_BREAKDOWNS })}</Hint>
        </div>
        <Boxes>
          {choices.length === 0 && <Hint>{t('survey.report.settings.noBreakdownChoices')}</Hint>}
          {choices.map((choice) => {
            const checked = isBreakdownChecked(form, choice);
            return (
              <CheckboxInput
                key={`${choice.kind}-${choice.questionId || ''}`}
                label={choice.label}
                checked={checked}
                disabled={!checked && full}
                onChange={(v) => setForm((f) => toggleBreakdown(f, choice, v))}
              />
            );
          })}
          {hasDepartmentGroup && (
            <CheckboxInput
              key="departmentGroup"
              label={t('survey.report.breakdown.departmentGroup')}
              checked
              onChange={(v) => { if (!v) setForm((f) => toggleBreakdown(f, { kind: 'departmentGroup', questionId: null }, false)); }}
            />
          )}
        </Boxes>

        <Divider />
        <div>
          <Button type="button" variant="ghost" aria-expanded={advancedOpen} onClick={() => setAdvancedOpen((v) => !v)}>
            {advancedOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {t('survey.report.settings.advanced')}
          </Button>
          <Hint>{t('survey.report.settings.advancedHint')}</Hint>
        </div>
        {advancedOpen && (
          <>
            <Row2>
              <TextInput label={t('survey.report.settings.favorableMin')} type="number" value={form.favorableMin}
                onChange={(e) => set({ favorableMin: e.target.value })} />
              <TextInput label={t('survey.report.settings.unfavorableMax')} type="number" value={form.unfavorableMax}
                onChange={(e) => set({ unfavorableMax: e.target.value })} />
              <TextInput label={t('survey.report.settings.strongMin')} type="number" value={form.strongMin}
                onChange={(e) => set({ strongMin: e.target.value })} />
              <TextInput label={t('survey.report.settings.watchMin')} type="number" value={form.watchMin}
                onChange={(e) => set({ watchMin: e.target.value })} />
            </Row2>
            <div>
              <TextInput label={t('survey.report.settings.heatmapBands')} value={form.heatmapBands}
                onChange={(e) => set({ heatmapBands: e.target.value })} />
              <Hint>{t('survey.report.settings.heatmapBandsHint')}</Hint>
            </div>
            {!publicSurvey && (
              <div>
                <TextInput label={t('survey.report.settings.tenureBands')} value={form.tenureBands}
                  onChange={(e) => set({ tenureBands: e.target.value })} />
                <Hint>{t('survey.report.settings.tenureBandsHint')}</Hint>
              </div>
            )}
          </>
        )}
      </Stack>
    </GenericFormDrawer>
  );
}
